import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onEdit, onPreview, onDuplicate, onDelete }) => {
  const [showActions, setShowActions] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-success/10 text-success';
      case 'draft':
        return 'bg-warning/10 text-warning';
      case 'archived':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className="group relative bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-250"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="aspect-video relative overflow-hidden bg-muted">
        <Image
          src={project?.thumbnail}
          alt={project?.thumbnailAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-250"
        />
        <div
          className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
            project?.status
          )}`}
        >
          {project?.status?.charAt(0)?.toUpperCase() + project?.status?.slice(1)}
        </div>
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground line-clamp-1">
            {project?.name}
          </h3>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              setShowActions(!showActions);
            }}
            className="lg:hidden p-1 rounded hover:bg-muted transition-colors"
            aria-label="Toggle actions"
          >
            <Icon name="MoreVertical" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{project?.description}</p>

        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <Icon name="Calendar" size={14} />
          <span>Modified {formatDate(project?.lastModified)}</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {project?.tags?.slice(0, 3)?.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs font-medium"
            >
              {tag}
            </span>
          ))}
          {project?.tags?.length > 3 && (
            <span className="text-xs text-muted-foreground">+{project?.tags?.length - 3}</span>
          )}
        </div>
      </div>
      <div
        className={`absolute inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center gap-2 transition-opacity duration-250 ${
          showActions ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <Button
          variant="default"
          size="sm"
          iconName="Edit"
          iconPosition="left"
          onClick={() => onEdit(project)}
        >
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onPreview(project)}
        >
          Preview
        </Button>
        <Button
          variant="ghost"
          size="icon"
          iconName="Copy"
          onClick={() => onDuplicate(project)}
          className="w-9 h-9"
        />
        <Button
          variant="ghost"
          size="icon"
          iconName="Trash2"
          onClick={() => onDelete(project)}
          className="w-9 h-9 text-destructive hover:text-destructive"
        />
      </div>
    </div>
  );
};

export default ProjectCard;